import {
  configureTelemetry,
  getLogger,
  createRapid7Sink,
  createConsoleSink,
  DEFAULT_PII_KEYS,
} from "@hoteltradergit/ht-telemetry-ui-lib";
import SecureSS from "../utils/SecureStorage";

const APP_NAME = "wallet-ui";
const isDev = process.env.NODE_ENV === "development";

/**
 * Configure the telemetry singleton once, at app boot.
 *
 * - Rapid7 sink (added only when REACT_APP_RAPID7_TOKEN is set) ships logs to
 *   InsightOps. The sink autoInits and only sends on *.hoteltrader.com origins
 *   by default, so local dev never hits Rapid7.
 * - Console sink in dev so engineers still see logs locally.
 * - PII redaction is mandatory; wallet-specific keys are added to the defaults.
 */
export const initTelemetry = () => {
  const token = process.env.REACT_APP_RAPID7_TOKEN;
  const region = process.env.REACT_APP_RAPID7_REGION || "us";

  const sinks = [];
  if (token) {
    sinks.push(createRapid7Sink({ token, region }));
  }
  if (isDev) {
    sinks.push(createConsoleSink());
  }

  configureTelemetry({
    sinks,
    defaultContext: { app: APP_NAME },
    redaction: {
      keys: [
        ...DEFAULT_PII_KEYS,
        "j_password",
        "currentPassword",
        "newPassword",
        "confirmPassword",
        "jwtToken",
        "cardCvv",
        "cvc",
        "accountNumber",
        "routingNumber",
      ],
    },
  });
};

/**
 * Lazy logger proxy. Resolves the active logger via getLogger() on every call
 * so it is safe to import and use in modules that are evaluated before
 * initTelemetry() runs (e.g. the axios instance created at import time).
 */
export const logger = {
  debug: (message, context) => getLogger().debug(message, context),
  info: (message, context) => getLogger().info(message, context),
  warn: (message, context) => getLogger().warn(message, context),
  error: (message, context) => getLogger().error(message, context),
};

/** Resolve the logged-in username for log context (never throws). */
const getUsername = () => {
  try {
    return SecureSS.getUserProfile()?.username || "anonymous";
  } catch {
    return "anonymous";
  }
};

/** Context shared by uncaught / error-boundary logs (user + page + agent). */
export const buildErrorContext = () => ({
  user_id: getUsername(),
  page_url: typeof window !== "undefined" ? window.location.href : "",
  user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
});

/** buildContext hook for the axios interceptor – attaches the current user. */
export const buildApiContext = () => ({ user_id: getUsername() });
